import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Worker : famille d'entités "personne qui travaille pour l'organisation".
 *
 * Couvre tous les statuts (salarié, bénévole, freelance, extra, stagiaire,
 * sous-traitant habituel) : le socle ne connaît PAS ces cas concrets, il connaît
 * la famille générique `worker`. Le vocabulaire métier est porté par le champ
 * `type` (chaîne libre déclarée par l'app), exactement comme pour asset.
 *
 * Périmètre v0 (org-wide, même doctrine qu'asset) : la fiche reste lisible par
 * l'org pour son identité de planification (nom, compétences, planifiabilité,
 * lieu de travail par défaut). Les données RH sensibles (statut contractuel,
 * coût horaire, dates d'embauche) NE SONT PAS dans ce schéma v0 : elles iront
 * dans un sous-document privé restreint aux rôles admin / RH (cf. WIP), jamais
 * org-wide.
 *
 * Convention de patron (cf. asset) :
 *   - `workerCoreFields`        : champs MÉTIER seuls (sans les champs standard).
 *   - `WorkerSchema`            : famille complète = champs standard + métier.
 *   - `WorkerCreateInputSchema` : entrée fournie par l'appelant à la création
 *     (les champs standard sont remplis par le SDK). Valide AVANT écriture.
 */

/** Champs métier de la famille worker (hors champs standard du socle). */
export const workerCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "cook", "server"). */
  type: z.string().min(1),
  /** Nom d'affichage de la personne (ex. "Ada Lovelace"). */
  name: z.string().min(1),
  /** Fiche `contact` liée (identité de la personne), optionnel. */
  contactId: z.string().min(1).optional(),
  /** Compétences : liste structurée pilotée par l'app, optionnel. */
  skills: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Réservable en ligne (planifiabilité publique), optionnel. */
  bookableOnline: z.boolean().optional(),
  /** Lieu de travail par défaut (référence `place`), optionnel. */
  defaultPlaceId: z.string().min(1).optional(),
  // status (statut contractuel : salarié, bénévole, freelance...) : sensible, ira dans un sous-document privé (cf. WIP), hors v0
  // hourlyCost : sensible, ira dans un sous-document privé (cf. WIP), hors v0
  // hiredAt : sensible, ira dans un sous-document privé (cf. WIP), hors v0
  // endedAt : sensible, ira dans un sous-document privé (cf. WIP), hors v0
} as const;

/** Schéma complet de la famille worker (champs standard + champs métier). */
export const WorkerSchema = z.object({
  ...baseEntityFields,
  ...workerCoreFields,
});

/** Type d'un worker. */
export type Worker = z.infer<typeof WorkerSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const WorkerCreateInputSchema = z.object({
  ...workerCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un worker. */
export type WorkerCreateInput = z.infer<typeof WorkerCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const WorkerUpdateInputSchema = z
  .object({ ...workerCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un worker. */
export type WorkerUpdateInput = z.infer<typeof WorkerUpdateInputSchema>;
